import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import styles from './FeaturedNewsOverview.module.scss';
import type {
  FeaturedNewsOverviewProps,
  FeaturedNewsOverviewRef,
} from './FeaturedNewsOverview.data';
import { findTransitionController } from 'util/react-transition-component';
import Heading from '../../atoms/heading/Heading';
import FeaturedNewsItem from './featured-news-item/FeaturedNewsItem';
import useScrollTrigger from '../../../hooks/useScrollTrigger';

export default function FeaturedNewsOverview({
  canTransitionIn,
  ref,
  ...props
}: FeaturedNewsOverviewProps & { ref?: Ref<FeaturedNewsOverviewRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const titleWrapperRef = useRef<HTMLDivElement | null>(null);
  const titleIsHidden = useRef(true);
  const progressRef = useRef(0);
  const canStartAnimation = useRef(canTransitionIn);

  const updateProgressAnimation = useCallback(() => {
    if (progressRef.current > 0 && progressRef.current < 0.1 && titleIsHidden.current) {
      const titleController = findTransitionController(titleRef);
      titleController?.transitionIn();
      titleIsHidden.current = false;
    } else if (progressRef.current >= 0.1 && !titleIsHidden.current) {
      const titleController = findTransitionController(titleRef);
      titleController?.transitionOut();
      titleIsHidden.current = true;
    }
  }, [progressRef, titleIsHidden]);

  const onCanTransitionIn = useCallback(() => {
    canStartAnimation.current = true;
    updateProgressAnimation();
  }, [updateProgressAnimation]);

  useEffect(() => {
    if (canTransitionIn) {
      onCanTransitionIn();
    }
  }, [canTransitionIn, onCanTransitionIn]);

  useScrollTrigger({
    trigger: elementRef,
    endTrigger: titleRef,
    pin: titleWrapperRef,
    vars: {
      scrub: false,
      start: 'top top',
      end: 'top top',
      pinType: 'transform',
      anticipatePin: 1,
      pinSpacing: false,
      async onUpdate({ progress }) {
        progressRef.current = progress;

        if (canStartAnimation.current) {
          updateProgressAnimation();
        }
      },
    },
  });

  return (
    <div
      ref={elementRef}
      className={classNames(
        styles.featuredNewsOverview,
        props.className,
        'no-padding-top-after-title',
      )}
      {...sbEditable(props.blok)}
    >
      <div ref={titleWrapperRef} className={classNames(styles.titleWrapper)}>
        <Heading
          ref={titleRef}
          transitionInType="splitCharsSlideInUp"
          transitionOutType="splitCharsSlideOutUp"
          className={classNames(styles.title)}
          as="span"
          size="heading-01"
          canTransitionIn={canTransitionIn}
          disableTriggerInOnScroll
        >
          {props.blok.title}
        </Heading>
      </div>

      <div className={classNames(styles.contentFrame, 'grid-gutter', 'max-content-width')}>
        {props.blok?.news_items?.map((item, index) => (
          <FeaturedNewsItem
            canTransitionIn={canTransitionIn}
            className={classNames(styles.newsItem)}
            blok={{ ...item }}
            alignRight={index % 2 === 0}
            key={item._uid}
          />
        ))}
      </div>
    </div>
  );
}
